import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useWishlist } from './wishlist/WishlistContext';
import { useProducts } from './useProducts';
import { findProductInCatalog } from './catalog/findProduct';
import CatalogImage from './catalog/CatalogImage';
import ProductPrice from './catalog/ProductPrice';
import { formatDisplayTitle, formatProductTitle } from './formatProductTitle';
import AddToCartButton from './cart/AddToCartButton';
import { productDetailPath } from './productPaths';
import { useSiteSettings } from './SiteSettingsContext';

function WishlistHeartIcon({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        d="M12 20.5l-1.1-1C5.5 14.2 2 11.1 2 7.25 2 4.5 4.2 2.5 6.9 2.5c1.6 0 3.1.8 4.1 2.1C12 3.3 13.4 2.5 15 2.5 17.7 2.5 20 4.5 20 7.25c0 3.85-3.5 6.95-8.9 12.25L12 20.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WishlistTrashIcon({ size = 16 }) {
  return (
    <svg
      className="wishlist-item__remove-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7h12z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function WishlistSkeleton() {
  return (
    <ul className="wishlist-items list-unstyled mb-0" aria-busy="true" aria-label="Loading saved items">
      {[1, 2, 3].map((i) => (
        <li key={i} className="wishlist-item wishlist-item--skeleton">
          <div className="wishlist-item__media skeleton-shimmer" />
          <div className="wishlist-item__body">
            <div className="skeleton-shimmer wishlist-skeleton__line wishlist-skeleton__line--title" />
            <div className="skeleton-shimmer wishlist-skeleton__line wishlist-skeleton__line--price" />
            <div className="skeleton-shimmer wishlist-skeleton__line wishlist-skeleton__line--btn" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function categoryLabel(categories, slug) {
  const hit = categories?.find((c) => c.slug === slug);
  return hit?.name ? formatDisplayTitle(hit.name) : formatDisplayTitle(slug || '');
}

export default function WishlistPage() {
  const { settings: site } = useSiteSettings();
  const store = site.store_copy || {};
  const { items, toggle, loading, isAuthed, serverEnabled } = useWishlist();
  const { products, categories } = useProducts();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const enriched = useMemo(
    () =>
      items.map((w) => {
        const live = findProductInCatalog(products, w.categorySlug, w.productId);
        return {
          ...w,
          live,
          name: live?.name || w.name,
          image: live?.image || w.image,
          unavailable: !live,
          categoryLabel: categoryLabel(categories, w.categorySlug),
        };
      }),
    [items, products, categories]
  );

  const availableCount = enriched.filter((x) => !x.unavailable).length;

  return (
    <div className="store-page wishlist-page">
      <Helmet>
        <title>{store.seo_wishlist_title}</title>
        {store.seo_wishlist_description ? (
          <meta name="description" content={store.seo_wishlist_description} />
        ) : null}
      </Helmet>

      <div className="container store-page__container">
        <header className="wishlist-hero">
          <div className="wishlist-hero__inner">
            <div className="wishlist-hero__icon" aria-hidden>
              <WishlistHeartIcon size={40} />
            </div>
            <div className="wishlist-hero__text">
              <p className="wishlist-hero__eyebrow">Your collection</p>
              <h1 className="wishlist-hero__title">Saved items</h1>
              <p className="wishlist-hero__lead">
                Curate pieces for Raksha Bandhan and gifting — compare, share, and add to your bag when you are ready.
                {isAuthed ? ' Synced with your account.' : ''}
              </p>
            </div>
            {items.length > 0 ? (
              <div className="wishlist-hero__stat" aria-label={`${items.length} saved items`}>
                <span className="wishlist-hero__stat-value">{items.length}</span>
                <span className="wishlist-hero__stat-label">{items.length === 1 ? 'saved' : 'saved'}</span>
              </div>
            ) : null}
          </div>
        </header>

        {!isAuthed ? (
          <div className="wishlist-banner wishlist-banner--sync" role="status">
            <div className="wishlist-banner__content">
              <p className="wishlist-banner__title">Save across devices</p>
              <p className="wishlist-banner__text mb-0">
                Items on this browser stay here until you sign in. Create an account to keep your list on phone and
                laptop.
              </p>
            </div>
            <div className="wishlist-banner__actions">
              <Link to="/login?next=/wishlist" className="btn btn-primary btn-sm rounded-pill px-3">
                Sign in
              </Link>
              <Link to="/register?next=/wishlist" className="btn btn-outline-secondary btn-sm rounded-pill px-3">
                Register
              </Link>
            </div>
          </div>
        ) : null}

        {!serverEnabled && isAuthed ? (
          <div className="wishlist-banner wishlist-banner--warn mb-4" role="alert">
            <p className="mb-0 small">
              Account sync needs the database migration <code>migrate_catalog_all.sql</code>. Items are
              stored on this device only until it is applied.
            </p>
          </div>
        ) : null}

        <div className="wishlist-layout">
          <div className="wishlist-layout__main">
            {loading ? (
              <WishlistSkeleton />
            ) : items.length === 0 ? (
              <div className="wishlist-empty-card">
                <div className="wishlist-empty-card__icon" aria-hidden>
                  <WishlistHeartIcon size={56} />
                </div>
                <h2 className="wishlist-empty-card__title">Nothing saved yet</h2>
                <p className="wishlist-empty-card__text">
                  Tap the heart on any product — in the shop or on a product page — to build your shortlist.
                </p>
                <Link to="/shop" className="btn btn-primary btn-lg rounded-pill store-cta">
                  Explore the shop
                </Link>
              </div>
            ) : (
              <>
                <ul className="wishlist-items list-unstyled mb-0">
                  {enriched.map((w) => (
                    <li
                      key={`${w.categorySlug}-${w.productId}`}
                      className={`wishlist-item${w.unavailable ? ' wishlist-item--unavailable' : ''}`}
                    >
                      <Link
                        to={productDetailPath({ category: w.categorySlug, id: w.productId })}
                        className="wishlist-item__media"
                      >
                        <CatalogImage
                          src={w.image}
                          alt={formatProductTitle(w.name)}
                          className="wishlist-item__img"
                        />
                      </Link>
                      <button
                        type="button"
                        className="wishlist-item__remove"
                        onClick={() =>
                          void toggle(
                            w.live || { id: w.productId, name: w.name, image: w.image, price: w.price },
                            w.categorySlug
                          )
                        }
                        aria-label={`Remove ${formatProductTitle(w.name)} from saved items`}
                      >
                        <WishlistTrashIcon size={15} />
                      </button>
                      <div className="wishlist-item__body">
                        <span className="wishlist-item__category">{w.categoryLabel}</span>
                        <h2 className="wishlist-item__title">
                          <Link to={productDetailPath({ category: w.categorySlug, id: w.productId })}>
                            {formatProductTitle(w.name)}
                          </Link>
                        </h2>
                        {w.live ? (
                          <ProductPrice product={w.live} className="wishlist-item__price" showBadge />
                        ) : (
                          <p className="wishlist-item__price wishlist-item__price--muted">Unavailable</p>
                        )}
                        {w.unavailable ? (
                          <p className="wishlist-item__note">This piece is no longer in our catalog.</p>
                        ) : null}
                        <div className="wishlist-item__footer">
                          {w.live ? (
                            <div className="wishlist-item__purchase">
                              <AddToCartButton
                                product={w.live}
                                categorySlug={w.categorySlug}
                                variant="inline"
                              />
                            </div>
                          ) : null}
                          <Link
                            to={productDetailPath({ category: w.categorySlug, id: w.productId })}
                            className="wishlist-item__details-link"
                          >
                            Details
                          </Link>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                {items.length > 0 ? (
                  <div className="wishlist-footer-bar">
                    <Link to="/shop" className="btn btn-outline-secondary rounded-pill">
                      Continue shopping
                    </Link>
                    <Link to="/cart" className="btn btn-primary rounded-pill">
                      View your bag
                    </Link>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <aside className="wishlist-layout__aside" aria-label="Tips">
            <div className="wishlist-aside-card">
              <p className="wishlist-aside-card__eyebrow">How it works</p>
              <h2 className="wishlist-aside-card__title">Your shortlist</h2>
              <ul className="wishlist-aside-card__list">
                <li>Save pieces while you browse — hearts appear on product pages and in collections.</li>
                <li>Compare styles and prices before you add to your bag.</li>
                <li>
                  {isAuthed
                    ? 'Your list is tied to your account and follows you when you sign in elsewhere.'
                    : 'Sign in to keep saved items when you change devices.'}
                </li>
              </ul>
              {!isAuthed ? (
                <Link to="/login?next=/wishlist" className="btn btn-primary w-100 rounded-pill">
                  Sign in to sync
                </Link>
              ) : (
                <Link to="/account" className="btn btn-outline-primary w-100 rounded-pill">
                  My account
                </Link>
              )}
            </div>

            {availableCount > 0 ? (
              <div className="wishlist-aside-card wishlist-aside-card--accent">
                <p className="wishlist-aside-card__eyebrow mb-2">Ready to order?</p>
                <p className="small mb-3 mb-md-0">
                  {availableCount} {availableCount === 1 ? 'piece is' : 'pieces are'} available to add to your bag.
                </p>
                <Link to="/cart" className="btn btn-primary w-100 rounded-pill mt-2">
                  Go to checkout
                </Link>
              </div>
            ) : null}
          </aside>
        </div>
      </div>
    </div>
  );
}
